import { Paper, Popper, PopperProps } from "@mui/material";
import { Z_INDEX_APP_BAR, padding } from "ds_legacy/materials/metrics";
import { focusOutlineStyles } from "ds_legacy/styles";
import { useFocusElement } from "dsl/hooks/useFocusElement";
import useFocusWithin from "dsl/hooks/useFocusWithin";
import { CSSProperties, ReactNode, useEffect, useRef, useState } from "react";
import styled from "styled-components";

// A descriptive and programmatic label MUST be provided for the triggering control using aria-label attribute or hidden off-screen text
export type ToggleTipProps = {
  ariaLabel: string;
  content: ReactNode;
  label: ReactNode;
  testId?: string;
  toggleTipStyle?: CSSProperties;
  toggleTipTriggerStyle?: CSSProperties;
  toggleTipWrapperStyle?: CSSProperties;
};

const StyledToggleTipTrigger = styled.button`
  background: none;
  box-sizing: border-box;
  border: none;
  cursor: pointer;
  padding: ${padding(0.25)};
  ${focusOutlineStyles()};
  &:focus-visible {
    text-decoration: underline;
  }
`;

const modifiers: PopperProps["modifiers"] = [
  {
    name: "preventOverflow",
    options: {
      altBoundary: true,
      boundary: "clippingParents", // The 'boundary: "clippingParents"' setting ensures that the Popover respects the boundaries of its parent elements with 'overflow: hidden'. It prevents the Popover from rendering outside these elements, effectively avoiding any overflow beyond areas hidden by parent's overflow settings.
      rootBoundary: "viewport",
    },
  },
];

/**
 * NOTE FOR DEVS: if any parent element of the ToggleTip has the CSS property overflow set to 'hidden', it can clip the ToggleTip
 */
export function ToggleTip({
  ariaLabel,
  content,
  label,
  testId,
  toggleTipStyle,
  toggleTipTriggerStyle,
  toggleTipWrapperStyle,
}: ToggleTipProps) {
  const ref = useRef<HTMLButtonElement | null>(null);
  const popperId = "popper_id";
  const [open, setOpen] = useState(false);
  const focusableElement = useFocusElement<HTMLDivElement>({
    dependencies: [open],
    restoreScrollPosition: true,
    setTabIndex: true,
    shouldFocus: open,
  });
  const { isFocusWithin, parentRef } = useFocusWithin<HTMLSpanElement>();

  const handleClick = () => {
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isFocusWithin && open) {
      setOpen(false);
    }
  }, [isFocusWithin, open]);

  return (
    <span ref={parentRef} style={toggleTipWrapperStyle}>
      {/* This could be replaced with an IconButton, or any other trigger component */}
      <StyledToggleTipTrigger
        ref={ref}
        aria-controls={popperId}
        aria-expanded={open}
        aria-label={ariaLabel}
        data-testid={testId ?? "toggle_tip"}
        style={toggleTipTriggerStyle}
        onClick={handleClick}
      >
        {label}
      </StyledToggleTipTrigger>
      {open && (
        <Popper
          ref={focusableElement}
          anchorEl={ref.current}
          disablePortal // make sure that the taborder follows logically, in case there's any focusable elements in the content
          id={popperId}
          modifiers={modifiers}
          open
          placement="auto"
          role={undefined}
          sx={{
            zIndex: Z_INDEX_APP_BAR - 1,
            ...toggleTipStyle,
          }}
        >
          <Paper elevation={4} sx={{ padding: padding(2) }}>
            {content}
          </Paper>
        </Popper>
      )}
    </span>
  );
}
